import React from 'react';
import { CTASection } from '../../components/CTASection';
import { FAQSection } from '../../components/FAQSection';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { TrendingUp, FileText } from 'lucide-react';
import { useSite } from '../../contexts/SiteContext';

export const LabralTears = () => {
  const { theme } = useSite();
  
  const faqs = [
    { question: 'What is a labral tear?', answer: 'The labrum is a ring of cartilage surrounding the glenoid (socket) that deepens the joint and provides stability. Tears can occur anteriorly (Bankart lesion - instability), superiorly (SLAP tears), or posteriorly, causing pain, clicking, and instability.' },
    { question: 'What causes labral tears?', answer: 'Common causes include: shoulder dislocations (Bankart tears), repetitive overhead motion (SLAP tears in athletes), falls on outstretched arm, degenerative changes with age, or acute trauma. The location and type of tear determine symptoms and treatment.' },
    { question: 'Do labral tears heal on their own?', answer: 'The labrum has poor blood supply and limited healing capacity. While some tears stabilize with conservative treatment, symptomatic tears causing instability or mechanical symptoms often require surgical repair for optimal outcomes.' },
    { question: 'What is the treatment?', answer: 'Conservative: Physiotherapy for rotator cuff and scapular strengthening, activity modification. Surgical: Arthroscopic labral repair using suture anchors. The specific technique depends on tear location and associated injuries.' }
  ];

  return (
    <div className="min-h-screen" data-testid="labral-tears-page">
      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: `${theme?.primaryHex}20`, color: theme?.primaryHex }}>
              Instability & Sports
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Shoulder Labral Tear Treatment in Hyderabad</h1>
            <p className="text-xl text-gray-600">SLAP & Bankart repair by Dr. Deepthi Nandan Reddy at Apollo Hospitals</p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Types of Labral Tears</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-t-4" style={{ borderTopColor: theme?.primaryHex }}>
                <CardHeader><CardTitle>Bankart Lesion</CardTitle></CardHeader>
                <CardContent className="text-gray-600">
                  <p>• Anterior-inferior labral tear</p>
                  <p>• Associated with dislocations</p>
                  <p>• Causes recurrent instability</p>
                  <p>• Often requires surgical repair</p>
                </CardContent>
              </Card>
              <Card className="border-t-4" style={{ borderTopColor: theme?.primaryHex }}>
                <CardHeader><CardTitle>SLAP Tear</CardTitle></CardHeader>
                <CardContent className="text-gray-600">
                  <p>• Superior labral tear</p>
                  <p>• Biceps anchor involved</p>
                  <p>• Common in throwers</p>
                  <p>• See dedicated SLAP page</p>
                </CardContent>
              </Card>
              <Card className="border-t-4" style={{ borderTopColor: theme?.primaryHex }}>
                <CardHeader><CardTitle>Posterior Labral Tear</CardTitle></CardHeader>
                <CardContent className="text-gray-600">
                  <p>• Less common</p>
                  <p>• Associated with posterior instability</p>
                  <p>• Internal impingement in throwers</p>
                  <p>• May need surgical repair</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-8 h-8" style={{ color: theme?.primaryHex }} />
              <h2 className="text-3xl font-bold text-gray-900">Treatment</h2>
            </div>
            <div className="space-y-4">
              <Card className="border-l-4 border-l-green-500">
                <CardHeader><CardTitle>Conservative Treatment</CardTitle></CardHeader>
                <CardContent className="text-gray-700">
                  <p>• Activity modification</p>
                  <p>• Rotator cuff strengthening</p>
                  <p>• Scapular stabilization</p>
                  <p>• Anti-inflammatory medications</p>
                  <p className="text-sm text-gray-500 mt-2">Appropriate for degenerative tears, stable lesions, or older patients</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-orange-500">
                <CardHeader><CardTitle>Surgical Repair</CardTitle></CardHeader>
                <CardContent className="text-gray-700">
                  <p>• Arthroscopic labral repair with suture anchors</p>
                  <p>• Address associated pathology (capsular laxity, bone loss)</p>
                  <p>• Bony procedures for significant glenoid bone loss (Latarjet)</p>
                  <p className="text-sm text-gray-500 mt-2">Success rate 85-95% for appropriately selected patients</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />
      <CTASection />
    </div>
  );
};

export default LabralTears;
